import { castArray, get } from 'lodash';
import type { MissingFieldsData } from '@/state/navigation';
import type {
  Card,
  CoverSheetConfig,
  Field,
} from '@/views/Coversheet/configurations/types';
import { getInvalidCardFields } from '@/views/Coversheet/hooks/useFieldValidator';
import { getCardFormDataPath } from './cardUtils';
import { doesFieldValidationApply } from './fieldValidation';
import { indexifyValues } from './indexifyValues';
import type { SanitizedCoverSheetFormData } from './transformCoverSheetToFormData';

const isFieldRequired = (field: Field, index: number) =>
  !!field.validation?.some(
    (v) => v.type === 'required' && doesFieldValidationApply(v, index),
  );

const getParentField = (card: Card, field: Field): Field | null => {
  if (!card.fields?.length) return null;
  if (field.showWhen) {
    const { fieldName } = field.showWhen;
    return card.fields.find((f) => f.name === fieldName) ?? null;
  }
  return (
    card.fields.find(
      (f) => f.name !== field.name && f.customField?.name === field.name,
    ) ?? null
  );
};

/**
 * Walks the cover-sheet config + form data and returns the same
 * `name -> MissingFieldsData` map that mounted field validators would build
 * up at runtime. Lets the missing-fields atom be populated from any route,
 * not only when the Cover Sheet page is mounted.
 *
 * `excludeCardIds` skips cards that the Cover Sheet page renders as field-less
 * substitutes (e.g. the unrepresented-buyer agent card), so the computed map
 * matches what the mounted validators would produce.
 */
export function computeMissingFields(
  config: CoverSheetConfig,
  formData: SanitizedCoverSheetFormData,
  excludeCardIds: string[] = [],
): Record<string, MissingFieldsData> {
  const missing: Record<string, MissingFieldsData> = {};
  const excluded = new Set(excludeCardIds);

  config.sections.forEach((section) => {
    section.cards.forEach((card) => {
      if (excluded.has(card.id)) return;
      const path = getCardFormDataPath(card);
      const cardRows = path
        ? castArray(get(formData, path, []) as unknown[])
        : [formData];

      // Match Card.tsx: when the card isn't addable or has no noEntryText,
      // there's always at least one rendered row to validate.
      let indices: number[] = [];
      if (cardRows.length > 0) {
        indices = cardRows.map((_, i) => i);
      } else if (!card.isAddable || !card.noEntryText) {
        indices = [0];
      }

      indices.forEach((index) => {
        const indexedCard = indexifyValues<Card>(card, index);
        const invalidFields = getInvalidCardFields(
          indexedCard,
          formData as Record<string, unknown>,
          index,
        );

        invalidFields.forEach((field) => {
          if (!isFieldRequired(field, index)) return;
          const parent = getParentField(indexedCard, field);
          missing[field.name] = {
            name: field.name,
            label: parent?.label ?? field.label,
            index,
            section: section.sidebarText,
            cardId: card.id,
            isAddable: card.isAddable ?? false,
            cardTitle: card.innerTitle ?? undefined,
          };
        });
      });
    });
  });

  return missing;
}
